import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Crown, Sparkles, Users, Trophy, Medal } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const monthLabel = (iso) => {
  try {
    return new Date(iso).toLocaleDateString('en-NG', {
      month: 'long',
      year: 'numeric',
    });
  } catch {
    return '';
  }
};

const rankColor = (rank) => {
  if (rank === 1) return { bg: 'bg-amber-100', border: 'border-amber-300', text: 'text-amber-700', Icon: Crown };
  if (rank === 2) return { bg: 'bg-slate-100', border: 'border-slate-300', text: 'text-slate-700', Icon: Trophy };
  if (rank === 3) return { bg: 'bg-orange-100', border: 'border-orange-300', text: 'text-orange-700', Icon: Medal };
  return { bg: 'bg-white', border: 'border-slate-200', text: 'text-slate-500', Icon: Users };
};

const TopInviters = () => {
  const { getAuthHeaders } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API_URL}/api/referral/top-inviters-monthly`, { headers: getAuthHeaders() })
      .then((r) => setData(r.data))
      .catch(() => setData({ top: [], my_entry: null, total_inviters: 0 }))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="space-y-3" data-testid="top-inviters-loading">
        <Skeleton className="h-20 rounded-2xl" />
        <Skeleton className="h-16 rounded-2xl" />
        <Skeleton className="h-16 rounded-2xl" />
      </div>
    );
  }

  const top = data?.top || [];
  const me = data?.my_entry;
  const hasAny = top.length > 0;

  return (
    <div className="space-y-3" data-testid="top-inviters-panel">
      {/* Header card */}
      <Card className="glass-card border-2 border-[#0B1F3A]/20">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="p-3 rounded-xl bg-[#0B1F3A] flex-shrink-0">
              <Sparkles size={20} className="text-white" />
            </div>
            <div className="flex-1">
              <p className="font-black text-[#0B1F3A] text-base">Top Inviters This Month</p>
              <p className="text-xs text-slate-600 font-medium">
                {monthLabel(data?.month_start)} &middot; Ranked by activated invites (invitees who made their first on-time contribution).
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* My rank */}
      {me && (
        <Card className="glass-card border-2 border-emerald-300 bg-emerald-50" data-testid="top-inviters-my-rank">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-[10px] text-emerald-700 font-bold uppercase tracking-wider">Your Rank</p>
              <p className="font-black text-emerald-900 text-2xl leading-tight">
                {me.rank ? `#${me.rank}` : '—'}
              </p>
            </div>
            <div className="text-right">
              <p className="text-[10px] text-emerald-700 font-bold uppercase tracking-wider">Activated Invites</p>
              <p className="font-black text-emerald-900 text-2xl leading-tight">{me.activated_count}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Leaderboard */}
      {hasAny ? (
        <div className="space-y-2">
          {top.map((row) => {
            const { bg, border, text, Icon } = rankColor(row.rank);
            return (
              <Card
                key={row.user_id}
                className={`${row.is_me ? 'border-2 border-emerald-400 bg-emerald-50' : `${bg} border ${border}`}`}
                data-testid={`top-inviter-row-${row.rank}`}
              >
                <CardContent className="p-3 flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center ${row.rank <= 3 ? text : 'text-slate-500'}`}>
                    <Icon size={18} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-bold text-slate-500 uppercase">Rank #{row.rank}</p>
                    <p className="font-bold text-slate-900 truncate">
                      {row.user_name}
                      {row.is_me && <span className="ml-1.5 text-emerald-700 text-xs font-black">(You)</span>}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="font-black text-[#0B1F3A] text-lg leading-tight">{row.activated_count}</p>
                    <p className="text-[9px] text-slate-500 font-semibold uppercase">invites</p>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card className="glass-card" data-testid="top-inviters-empty">
          <CardContent className="p-6 text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-slate-100 mb-2">
              <Users size={20} className="text-slate-400" />
            </div>
            <p className="font-bold text-[#0B1F3A]">No activated invites yet this month</p>
            <p className="text-xs text-slate-600 font-medium mt-1">
              Share a group via WhatsApp — when a friend joins and makes their first on-time contribution, you climb this board and earn Trust Points.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default TopInviters;
